import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { SocialAuthService, SocialUser } from 'angularx-social-login';
import { FacebookLoginProvider } from 'angularx-social-login';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/auth/services/auth.service';
import { CredentialsSocial } from 'src/app/core/auth/models/credentialsSocial';
import { Router } from '@angular/router';
import { rememberMeLogin, snackBarConfig, success } from './../../../app.constants';
import { ClientService } from 'src/app/services/client/client.service';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as uuid from 'uuid';
import { loginUUID } from './../../../app.constants';

declare const google: any;

@Component({
  selector: 'aum-cli-social-login-registration',
  templateUrl: './social-login-registration.component.html',
  styleUrls: ['./social-login-registration.component.scss']
})
export class SocialLoginRegistrationComponent {
  @Input() form: FormGroup;
  @Input() socialLogin = { value: false };
  @Input() isLoginScreen;
  socialLoginSubscription: Subscription;
  showSpinner = false;

  constructor(
    private socialAuthService: SocialAuthService,
    private authService: AuthService,
    private router: Router,
    private http: HttpClient,
    private clientService: ClientService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {
    this.initGoogleAuth();
  }

  initGoogleAuth(): void {
    try {
      google.accounts.id.initialize({
        client_id:
          '994111026382-ib93pbsiqikrk0uu65leesfof3poen8k.apps.googleusercontent.com',
        callback: response => this.onGoogleSignIn(response)
      });
    } catch (error) {
      console.error('Google sign-in error:', error);
      window.location.reload();
    }
  }

  onGoogleSignIn(response: any): void {
    try {
      console.log('Full Google Response:', response);
      if (response && response.credential) {
        const user: SocialUser = new SocialUser();
        user.idToken = response.credential;
        user.provider = 'GOOGLE';
        this.trackRegisterState(response.credential, 'GOOGLE');
        // this.trackAuthState(user);
      } else {
        console.error('No credential received from Google sign-in', response);
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
    }
  }

  signInWithGoogle(): void {
    try {
      google.accounts.id.prompt(response => {
        this.onGoogleSignIn(response);
      });
    } catch (error) {
      console.error('Google sign-in error:', error);
      window.location.reload();
    }
  }

  signInWithFB(): void {
    this.socialAuthService
      .signIn(FacebookLoginProvider.PROVIDER_ID)
      .then((user: SocialUser) => {
        this.trackAuthState(user);
      });
  }

  login(provider: string, authToken: string): void {
    localStorage.setItem(loginUUID, uuid.v4());
    try {
      this.showSpinner = true;
      const credentialsSocial: CredentialsSocial = {
        provider,
        authToken
      };
      this.authService.socialLogin(credentialsSocial).subscribe(
        data => {
          try {
            this.showSpinner = false;
            localStorage.setItem(rememberMeLogin, this.form.value.rememberMe);
            this.authService.setSession(data.access_token, data.refresh_token);
            this.socialAuthService.signOut();
            this.clientService.pushToken().subscribe(
              () => {
                try {
                  this.router.navigate(['/'], { replaceUrl: true });
                } catch (e) {
                  console.error('Routing failed', e);
                }
              },
              error => {
                console.error('Error during token push:', error);
              }
            );
          } catch (e) {
            window.location.reload();
          }
        },
        error => {
          console.error('Login error:', error);
          this.showSpinner = false;
          this.snackBar.open(
            this.translateService.instant('error.socialAccountNotLinked'),
            this.translateService.instant(success),
            snackBarConfig
          );
        }
      );
    } catch (e) {
      window.location.reload();
    }
  }

  trackAuthState(user: SocialUser) {
    try {
      console.log('Tracking State with User:', user);
      if (!user) return;
      const { idToken, lastName, firstName, email, provider } = user;
      this.showSpinner = false;
      if (!this.isLoginScreen) {
        this.socialLogin.value = true;
        this.form.controls.password.disable();
        this.form.controls.confirmPassword.disable();

        this.form.setValue({
          authToken: idToken,
          lastName,
          email,
          firstName,
          loginProvider: provider,
          phoneNumber: null,
          password: null,
          confirmPassword: null,
          termsCondition: false,
          countryCode: null
        });
      } else {
        this.login(provider, idToken);
      }
    } catch (e) {
      window.location.reload();
    }
  }

  trackRegisterState(idToken: string, provider: string) {
    try {
      this.showSpinner = true;
      if (!this.isLoginScreen) {
        this.getGoogleUser(idToken).subscribe(userInfo => {
          console.log(userInfo);
          this.showSpinner = false;
          if (userInfo) {
            this.socialLogin.value = true;
            this.form.controls.password.disable();
            this.form.controls.confirmPassword.disable();

            this.form.setValue({
              authToken: idToken,
              loginProvider: provider,
              lastName: userInfo.family_name,
              email: userInfo.email,
              firstName: userInfo.given_name,
              sub: userInfo.sub,
              phoneNumber: null,
              password: null,
              confirmPassword: null,
              termsCondition: false,
              countryCode: null
            });
          } else {
            console.error('Failed to retrieve user info');
          }
        });
      } else {
        this.login(provider, idToken);
      }
    } catch (e) {
      window.location.reload();
    }
  }

  getGoogleUser(accessToken: string): Observable<any | null> {
    try {
      const url = `https://www.googleapis.com/oauth2/v3/tokeninfo?id_token=${accessToken}`;
      return this.http.get<UserInfo>(url).pipe(
        catchError(error => {
          console.error('Error retrieving user info from Google:', error);
          return of(null);
        })
      );
    } catch (e) {
      return null;
    }
  }
}

interface UserInfo {
  email?: string;
  given_name?: string;
  family_name?: string;
  sub?: string;
  phone_number?: string;
}
